import { MarketingForgeElementType } from "@redotech/redo-content-builder-common/element-schema";
import {
  SPOTLIGHT_LAUNCHER_LOCALIZATION_KEY,
  SpotlightElement,
  SpotlightForm,
  SpotlightLocalizations,
  SpotlightStep,
} from "@redotech/redo-spotlight-common/spotlight-schema";
import { deepCopyObject } from "@redotech/util/object";

type ElementOverlay = Record<string, Record<string, string>>;

/**
 * Returns a copy of the spotlight with its merchant-authored copy replaced by
 * the translations stored under `language` — keyed by element `_id` (and the
 * reserved "launcher" key), then field name. Any element or field without a
 * translation keeps its authored value, and when no overlay applies (no
 * language, or no translations for it) the original spotlight is returned
 * unchanged by reference, so an un-translated spotlight renders exactly as
 * before.
 */
export function localizeSpotlight(
  spotlight: SpotlightForm,
  language: string | undefined,
): SpotlightForm {
  const overlay = language ? spotlight.localizations[language] : undefined;
  if (!overlay || Object.keys(overlay).length === 0) {
    return spotlight;
  }

  // deepCopyObject instead of structuredClone: this runs in the storefront
  // widget (older Safari lacks structuredClone) and in jsdom tests.
  const localized = deepCopyObject(spotlight);
  for (const step of localized.steps) {
    localizeElements(step.elements, overlay);
  }
  if (localized.launcher) {
    const launcherText = overlay[SPOTLIGHT_LAUNCHER_LOCALIZATION_KEY]?.text;
    if (launcherText !== undefined) {
      localized.launcher.text = launcherText;
    }
  }
  return localized;
}

/**
 * Returns a copy of a single step with its element copy replaced by the
 * translations for `language` — same no-op-by-reference behavior as
 * localizeSpotlight. Used by the builder preview to render a translated
 * example.
 *
 * `skipElementId` leaves one element's copy in the authored (default) language
 * even while previewing a translation, so the builder can render the element
 * the merchant is currently editing in the default language for editing.
 */
export function localizeStep(
  step: SpotlightStep,
  language: string | undefined,
  localizations: SpotlightLocalizations,
  skipElementId?: string,
): SpotlightStep {
  const overlay = language ? localizations[language] : undefined;
  if (!overlay || Object.keys(overlay).length === 0) {
    return step;
  }
  const localized = deepCopyObject(step);
  localizeElements(localized.elements, overlay, skipElementId);
  return localized;
}

function localizeElements(
  elements: SpotlightElement[],
  overlay: ElementOverlay,
  skipElementId?: string,
): void {
  for (const element of elements) {
    const fields =
      element._id && element._id !== skipElementId
        ? overlay[element._id]
        : undefined;
    if (fields) {
      if ("text" in element && typeof fields.text === "string") {
        element.text = fields.text;
      }
      if ("html" in element && typeof fields.html === "string") {
        element.html = fields.html;
      }
      if (
        "placeholderText" in element &&
        typeof fields.placeholderText === "string"
      ) {
        element.placeholderText = fields.placeholderText;
      }
    }
    if (
      element.type === MarketingForgeElementType.CONTAINER ||
      element.type === MarketingForgeElementType.COLUMN
    ) {
      localizeElements(element.elements, overlay, skipElementId);
    }
  }
}
